import {
  Badge,
  Body1Strong,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
} from '@fluentui/react-components';
import { Dismiss24Regular, Open24Regular } from '@fluentui/react-icons';
import { ButtonLoading, ControlledField, ControlledTooltip } from '@shared/components';
import { useContext, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';

import { formatSubmissionStatus } from '../../SubmissionStatusBadge';
import { useUpdateSubmissionItem } from '../../../api';
import { ESubmissionItemStatus, TUpdateSubmissionItemDto } from '../../../types/submissions.types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useVersionInfo } from '@modules/items';
import { TUpdateSubmissionItemDialogState } from '../../../types/dialogs.types';
import { Link } from 'react-router-dom';
import { ALL_SUBMISSION_ITEM_STATES } from '../../../constants';
import { SubmissionItemStatusIcon } from '../items/SubmissionItemStatus';
import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { ApprovalStatus } from '@modules/approvals';
import { SignInContext, USER_GROUPS } from '@modules/users';
import { useHasAssigneePermissions, useHasFullPermissions } from '../../../helpers/getPermissions.helper';

type TProps = {
  state?: TUpdateSubmissionItemDialogState;
  setState: (state: TUpdateSubmissionItemDialogState) => void;
  isItemView?: boolean;
};

// --- SCHEMA & FORM:
const schema = yup.object({
  status: yup
    .array()
    .of(yup.mixed<ESubmissionItemStatus>().oneOf(ALL_SUBMISSION_ITEM_STATES))
    .max(1, 'You can only have one status.'),
  assignee: yup.string().nullable(),
  dueDate: yup.string().nullable(),
  submittedVersionId: yup
    .array()
    .of(yup.string())
    .max(1, 'You can only select one version.')
    .when('status', ([status], s) => {
      const accepted = status.length > 0 && status[0] === ESubmissionItemStatus.ACCEPTED;
      return accepted ? s.min(1, 'Select a version.') : s;
    }),
});

type TFormData = yup.InferType<typeof schema>;

const UpdateSubmissionItemDialog = ({ state, setState, isItemView = false }: TProps) => {
  const { user } = useContext(SignInContext);
  const newStatusText = state?.newStatus ? formatSubmissionStatus(state.newStatus) : '';
  const itemName = state?.item.name || '';

  // --- PERMISSIONS:
  const hasFullPermissions = useHasFullPermissions(state?.submissionInfo);
  const hasAssigneePermissions = useHasAssigneePermissions(hasFullPermissions, state?.submissionItem);

  // --- SCHEMA & FORM:
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      status: [],
      assignee: null,
      dueDate: null,
      submittedVersionId: [],
    },
  });

  // --- UPDATE SUBMISSION ITEM:
  const updateSubmissionItem = useUpdateSubmissionItem();

  const onSubmit = async (formData: TFormData) => {
    if (!state) return;

    const updateData: TUpdateSubmissionItemDto = {};
    if (formData.status?.[0] !== state.submissionItem.status) updateData.newStatus = formData.status?.[0];
    if (formData.assignee !== (state.submissionItem.assignee || null)) updateData.assignee = formData.assignee || null;
    if (formData.dueDate !== (state.submissionItem.dueDate || null)) updateData.dueDate = formData.dueDate || null;
    if (formData.submittedVersionId?.[0] !== (state.submissionItem.submittedVersionId || null))
      updateData.submittedVersionId = formData.submittedVersionId?.[0] || null;

    updateSubmissionItem.mutate(
      {
        submissionId: state.submissionInfo._id,
        submissionItemId: state.submissionItem.item,
        updateData,
      },
      {
        onSuccess: () => {
          setState({ ...state, isOpen: false });
        },
        onError: (error) => {
          console.error('Error updating submission item:', error);
        },
      }
    );
  };

  // --- RENDER:

  const { data: versionInfo } = useVersionInfo(state?.submissionItem?.item, state?.submissionItem?.itemType);
  const versionIds = useMemo(
    () => (versionInfo ? versionInfo.versions.map((version) => version.id) : []),
    [versionInfo]
  );
  const approvedVersionIdSet = useMemo(
    () =>
      new Set(
        versionInfo
          ? versionInfo.approvals
              .filter((approval) => approval.status === ApprovalStatus.APPROVED)
              .map((approval) => approval.versionId)
          : []
      ),
    [versionInfo]
  );

  useEffect(() => {
    if (state?.isOpen)
      reset({
        status: [state.newStatus || state.submissionItem.status],
        assignee:
          state.submissionItem.assignee ||
          (state.newStatus === ESubmissionItemStatus.IN_PROGRESS && state.item.stats.authors[0]) ||
          null,
        dueDate: state.submissionItem.dueDate || null,
        submittedVersionId: state.submissionItem.submittedVersionId ? [state.submissionItem.submittedVersionId] : [],
      });
  }, [state?.isOpen]);

  const statusValueOfForm = watch('status');
  const statusOfForm = statusValueOfForm?.[0] || ESubmissionItemStatus.BACKLOG;

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(e, data) => {
        e.stopPropagation();
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              {state &&
                (isItemView ? (
                  <span className="flex items-center space-x-1">
                    <span>{state.submissionInfo.title}</span>
                    <Link to={`/product/${state.submissionInfo.product}/submissions/${state.submissionInfo._id}`}>
                      <Button icon={<Open24Regular />} appearance="subtle" />
                    </Link>
                  </span>
                ) : newStatusText ? (
                  `${newStatusText}: ${state.item.name}`
                ) : (
                  `Update ${itemName}`
                ))}
            </DialogTitle>

            <DialogContent className="flex flex-col min-h-60">
              {state && user._id === state.submissionItem.assignee && (
                <MessageBar intent="info" className="mb-4">
                  <MessageBarBody>
                    You have been assigned to prepare this file for submission. Change the status to{' '}
                    <Body1Strong>In review</Body1Strong> after you are finished.
                  </MessageBarBody>
                </MessageBar>
              )}
              <ControlledField
                name="assignee"
                inputType="multiuserselect"
                control={control}
                errors={errors}
                label="Assignee"
                fixedHeight
                isSingle
                tooltip={!hasAssigneePermissions ? 'You do not have the required permissions.' : undefined}
                readonly={!hasAssigneePermissions}
              />

              <div className="grid grid-cols-2 gap-x-3">
                {state?.newStatus !== ESubmissionItemStatus.ACCEPTED && (
                  <ControlledField
                    name="dueDate"
                    inputType="date"
                    control={control}
                    errors={errors}
                    label="Due date"
                    allowTextInput
                    fixedHeight
                    tooltip={!hasFullPermissions ? 'Only submission leads or admins change the due date.' : undefined}
                    readonly={!hasFullPermissions}
                  />
                )}

                {[undefined, ESubmissionItemStatus.IN_REVIEW, ESubmissionItemStatus.ACCEPTED].includes(
                  state?.newStatus
                ) && (
                  <ControlledField
                    inputType="dropdown"
                    label="Version to submit"
                    placeholder="Select version"
                    name="submittedVersionId"
                    getLabel={(val) => `${val} (${approvedVersionIdSet.has(val) ? 'approved' : 'not approved'})`}
                    clearable
                    control={control}
                    errors={errors}
                    fixedHeight
                    required={statusOfForm === ESubmissionItemStatus.ACCEPTED}
                    tooltip={!hasAssigneePermissions ? 'You do not have the required permissions.' : undefined}
                    readonly={!hasAssigneePermissions}
                  >
                    <div className="max-h-80 overflow-y-auto">
                      {versionIds.map((versionId) => {
                        const isApproved = approvedVersionIdSet.has(versionId);
                        return (
                          <Option key={versionId} value={versionId} text={versionId}>
                            <div className="w-full flex justify-between items-center pr-4">
                              <span>{versionId}</span>
                              <Badge color={isApproved ? 'brand' : 'danger'} appearance="tint" shape="rounded">
                                {!isApproved && 'not '}approved
                              </Badge>
                            </div>
                          </Option>
                        );
                      })}
                    </div>
                  </ControlledField>
                )}

                {!newStatusText && (
                  <ControlledField
                    button={
                      <p className="flex items-center space-x-1">
                        <SubmissionItemStatusIcon status={statusOfForm} size="medium" />
                        <span>{formatSubmissionStatus(statusOfForm)}</span>
                      </p>
                    }
                    inputType="dropdown"
                    required
                    label="Status"
                    placeholder="Select status"
                    name="status"
                    getLabel={formatSubmissionStatus}
                    control={control}
                    errors={errors}
                    fixedHeight
                    tooltip={!hasAssigneePermissions ? 'You do not have the required permissions.' : undefined}
                    readonly={!hasAssigneePermissions}
                  >
                    {ALL_SUBMISSION_ITEM_STATES.map((status) => (
                      <ControlledTooltip
                        key={status}
                        enable={status === ESubmissionItemStatus.ACCEPTED && !hasFullPermissions}
                        content="You do not have the required permissions."
                        relationship="inaccessible"
                      >
                        <Option
                          value={status}
                          text={formatSubmissionStatus(status)}
                          disabled={status === ESubmissionItemStatus.ACCEPTED && !hasFullPermissions}
                        >
                          <span className="flex items-center space-x-1">
                            <SubmissionItemStatusIcon status={status} size="medium" />
                            <span>{formatSubmissionStatus(status)}</span>
                          </span>
                        </Option>
                      </ControlledTooltip>
                    ))}
                  </ControlledField>
                )}
              </div>
            </DialogContent>

            <DialogActions>
              <ControlledTooltip
                enable={!hasAssigneePermissions}
                content="You do not have the required permissions."
                relationship="inaccessible"
              >
                <ButtonLoading
                  type="submit"
                  appearance="primary"
                  loading={updateSubmissionItem.isPending}
                  disabled={!hasAssigneePermissions}
                >
                  {newStatusText ? newStatusText : 'Update'}
                </ButtonLoading>
              </ControlledTooltip>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default UpdateSubmissionItemDialog;
